import axios from "axios";
import { isLogged } from "@/middleware/Auth.js";

export const state = {
  currentUser: getSavedState("auth.currentUser")
};

export const mutations = {
  SET_CURRENT_USER(state, newValue) {
    state.currentUser = newValue;
    saveState("auth.currentUser", newValue);
    setDefaultAuthHeaders(state);
  }
};

export const getters = {
  // Whether the user is currently logged in.
  loggedIn(state) {
    return !!state.currentUser;
  },
  currentUser(state) {
    return state.currentUser;
  },
  getUserRoles(state) {
    return state.currentUser ? state.currentUser.roles : [];
  },
  getUserPermissions(state) {
    return state.currentUser ? state.currentUser.special_permissions : [];
  },
  currentToken(state) {
    return state.currentUser ? state.currentUser.token : "";
  }
};

export const actions = {
  // This is automatically run in `src/state/store.js` when the app
  // starts, along with any other actions named `init` in other modules.
  init({ state, dispatch }) {
    setDefaultAuthHeaders(state);
    dispatch("validate");
  },

  // Logs in the current user.
  logIn({ commit, dispatch }, { email, password } = {}) {
    if (isLogged()) return dispatch("validate");
    return axios
      .post(process.env.VUE_APP_NKRSYNC_BE + "auth/login", { email, password })
      .then(async response => {
        const user = response.data;
        commit("SET_CURRENT_USER", user);
        // Reload general data (departments, marketplaces, etc.) after login
        // and wait for it so the menu has permissions/departments before navigating
        await dispatch("general/init", null, { root: true });
        return true;
      })
      .catch(e => {
        throw e;
      });
  },

  // Logs out the current user.
  logOut({ commit }) {
    commit("SET_CURRENT_USER", null);
  },

  // Validates the current user's token and refreshes it
  // with new data from the API.
  validate({ commit }) {
    if (!isLogged()) return Promise.resolve(null);

    return axios
      .get(process.env.VUE_APP_NKRSYNC_BE + "auth/refresh_token")
      .then(response => {
        const user = response.data;
        commit("SET_CURRENT_USER", user);
        return user;
      })
      .catch(error => {
        if (error.response && error.response.status === 401) {
          commit("SET_CURRENT_USER", null);
        } else {
          // eslint-disable-next-line no-console
          console.warn(error);
        }
        return null;
      });
  }
};

// ===
// Private helpers
// ===

function getSavedState(key) {
  try {
    return JSON.parse(window.localStorage.getItem(key));
  } catch (e) {
    return false;
  }
}

function saveState(key, state) {
  window.localStorage.setItem(key, JSON.stringify(state));
}

function setDefaultAuthHeaders(state) {
  axios.defaults.headers.common.Authorization = state.currentUser
    ? state.currentUser.token
    : "";
}
